import { SWS_IMAGES } from '@/constants';
import type { CountryISO } from '@/constants/countries';
import type { CurrencyISO } from '@/constants/currencies';
import { countries } from '@/constants/countries';
import {
  PRIMARY_INDUSTRIES,
  SECONDARY_INDUSTRIES,
  SECTORS,
  TERTIARY_INDUSTRIES,
} from '@/constants/industries';
import { getStaticUrl, BASE_URL } from '@/hooks/useCloudflareImage';
import { MarketPageType } from '@/pages/Market/hooks/useReduxStateMarkets/types';

const CFCountryImageMap = {
  aed: 'ae',
  ats: 'at',
  aud: 'au',
  bdt: 'bd',
  bef: 'be',
  bhd: 'bh',
  brl: 'br',
  bwp: 'bw',
  cad: 'ca',
  chf: 'ch',
  cny: 'cn',
  czk: 'cz',
  dem: 'de',
  dkk: 'dk',
  egp: 'eg',
  esp: 'es',
  eur: 'eu',
  fim: 'fi',
  frf: 'fr',
  gbp: 'gb',
  grd: 'gr',
  hkd: 'hk',
  huf: 'hu',
  idr: 'id',
  iep: 'ie',
  inr: 'in',
  itl: 'it',
  jpy: 'jp',
  krw: 'kr',
  luf: 'lu',
  mxn: 'mx',
  myr: 'my',
  nlg: 'nl',
  nok: 'no',
  nzd: 'nz',
  pln: 'pl',
  pte: 'pt',
  rub: 'ru',
  rur: 'ru',
  sar: 'sa',
  sek: 'se',
  sgd: 'sg',
  thb: 'th',
  trl: 'tr',
  twd: 'tw',
  usd: 'us',
  zar: 'za',
} as const;

export function getGridViewCoverFromImageUrl(imageUrl: string | null) {
  if (imageUrl === null) return getStaticUrl('blank.jpg');
  return `${BASE_URL}${imageUrl}`;
}

export function getProviderImageFromName(name: string) {
  const imageMap = {
    classsuper: 'ClassSuper.png',
    drivewealth: 'DriveWealth.png',
    dummy: 'Dummy.png',
    etrade: 'Etrade.png',
    fidelity: 'Fidelity.png',
    ib: 'IB.png',
    optionshouse: 'OptionsHouse.png',
    questrade: 'Questrade.png',
    robinhood: 'Robinhood.png',
    schwab: 'Schwab.png',
    scottrade: 'Scottrade.png',
    sharesight: 'ShareSight.png',
    stake: 'Stake.png',
    stocktwits: 'StockTwits.png',
    td: 'Td.png',
    tradeit: 'TradeIt.png',
    tradeking: 'TradeKing.png',
    tradestation: 'TradeStation.png',
    tradier: 'Tradier.png',
    vanguard: 'Vanguard.png',
  };
  const safeName = name.toLowerCase();
  if (safeName in imageMap) return getStaticUrl(imageMap[safeName]);
  return getStaticUrl('blank.jpg');
}

export function getMarketCoverFromCurrency(currencyISO?: CurrencyISO) {
  const blank = getStaticUrl('blank.jpg');
  if (typeof currencyISO === 'undefined') return blank;
  const safeISO = currencyISO.toLowerCase();
  if (safeISO in CFCountryImageMap) {
    return getStaticUrl(`${CFCountryImageMap[safeISO]}.jpg`);
  }
  return blank;
}

export function getMarketCoverFromCountryISO(countryISO?: CountryISO) {
  const blank = getStaticUrl('stock-market.jpg');
  if (typeof countryISO === 'undefined') return blank;
  const safeISO = countryISO.toLowerCase();
  const hasCountryImage =
    Object.values<string>(CFCountryImageMap).includes(safeISO);
  if (safeISO in countries && hasCountryImage) {
    return getStaticUrl(`${safeISO}.jpg`);
  }
  return blank;
}

export function getIndustryCoverFromId(id: number, pageType: MarketPageType) {
  const blank = getStaticUrl('stock-market.jpg');
  if (id === 0) return blank;
  const safeId = id.toString();
  if (pageType === MarketPageType.SECTOR && safeId in SECTORS) {
    const firstChildId = SECTORS[safeId].children[0];
    if (firstChildId) {
      return getStaticUrl(`industries/${firstChildId}.jpg`);
    }
  }
  if (pageType === MarketPageType.SECONDARY && safeId in SECONDARY_INDUSTRIES) {
    const firstChildId = SECONDARY_INDUSTRIES[safeId].children[0];
    if (firstChildId) {
      return getStaticUrl(`industries/${firstChildId}.jpg`);
    }
  }
  if (
    safeId in PRIMARY_INDUSTRIES ||
    (pageType === MarketPageType.TERTIARY && safeId in TERTIARY_INDUSTRIES)
  ) {
    return getStaticUrl(`industries/${safeId}.jpg`);
  }
  return blank;
}

export enum CoverSize {
  MAIN_HEADER,
  MAIN_THUMB,
  DASHBOARD_THUMB,
}

export function getCoverImageByCompanyId(
  companyId: string,
  size: CoverSize = CoverSize.MAIN_THUMB
) {
  let sizeQuery = '';
  switch (size) {
    case CoverSize.MAIN_HEADER:
      sizeQuery += '?size=main-header';
      break;
    case CoverSize.MAIN_THUMB:
      sizeQuery += '?size=main-thumb';
      break;
    case CoverSize.DASHBOARD_THUMB:
      sizeQuery += '?size=dashboard-thumb';
      break;
  }
  if (companyId) {
    return `${SWS_IMAGES}/company/${companyId}/cover${sizeQuery}`;
  } else {
    return '';
  }
}
